/**
 * Телефоны по НФТ-09: показ «+7 7XX XXX XX XX», хранение и API — E.164 «+77011234567»
 * (docs/contracts/backend-conventions.md). Номера Казахстана начинаются с 7 после кода страны.
 */
const COUNTRY_CODE = '7';
const NATIONAL_LENGTH = 10;
/** Группы национального номера: 3-3-2-2. */
const GROUPS = [3, 3, 2, 2] as const;

export function digitsOf(value: string): string {
  return value.replace(/\D/g, '');
}

/**
 * Национальная часть номера (до 10 цифр) из любого привычного ввода:
 * «+7 701 123 45 67», «8 (701) 123-45-67», «77011234567», «7011234567».
 */
export function nationalDigits(value: string): string {
  const trimmed = value.trim();
  let digits = digitsOf(trimmed);
  if (trimmed.startsWith('+')) {
    if (digits.startsWith(COUNTRY_CODE)) digits = digits.slice(1);
  } else if (digits.length === NATIONAL_LENGTH + 1 && (digits.startsWith('7') || digits.startsWith('8'))) {
    digits = digits.slice(1);
  }
  return digits.slice(0, NATIONAL_LENGTH);
}

/** Любой ввод → E.164 «+77011234567» или null, если номер неполный. */
export function normalizePhone(value: string): string | null {
  const national = nationalDigits(value);
  return national.length === NATIONAL_LENGTH ? `+${COUNTRY_CODE}${national}` : null;
}

/** Номер Казахстана в E.164: +7 и 10 цифр, первая — 7. */
export function isKazakhstanPhone(value: string): boolean {
  return /^\+77\d{9}$/.test(value);
}

/** Частично введённый национальный номер → «+7 701 123 45 67» по мере ввода. */
export function formatNationalProgressive(national: string): string {
  const digits = digitsOf(national).slice(0, NATIONAL_LENGTH);
  let result = `+${COUNTRY_CODE}`;
  let offset = 0;
  for (const size of GROUPS) {
    if (offset >= digits.length) break;
    result += ` ${digits.slice(offset, offset + size)}`;
    offset += size;
  }
  return result;
}

/** E.164 → «+7 701 123 45 67». Другие форматы возвращаются как есть. */
export function formatPhone(value: string | null | undefined): string {
  if (!value) return '';
  const national = /^\+7(\d{10})$/.exec(value)?.[1];
  return national ? formatNationalProgressive(national) : value;
}

/**
 * Позиция каретки в отформатированной строке после N-й цифры национального номера
 * (цифры кода страны «+7» не считаются). Нужна полю PhoneInput, чтобы каретка не прыгала.
 */
export function caretAfterNationalDigit(formatted: string, digitCount: number): number {
  const prefixLength = `+${COUNTRY_CODE}`.length;
  if (digitCount <= 0) return Math.min(formatted.length, prefixLength + 1);
  let seen = 0;
  for (let index = prefixLength; index < formatted.length; index += 1) {
    if (/\d/.test(formatted[index] ?? '')) {
      seen += 1;
      if (seen === digitCount) return index + 1;
    }
  }
  return formatted.length;
}

/** Маска для показа последних цифр без раскрытия номера целиком: «+7 7•• ••• •• 67». */
export function maskPhone(value: string): string {
  const national = /^\+7(\d{10})$/.exec(value)?.[1];
  if (!national) return value;
  let countrySkipped = false;
  let nationalIndex = -1;
  return formatNationalProgressive(national).replace(/\d/g, (digit) => {
    if (!countrySkipped) {
      countrySkipped = true;
      return digit;
    }
    nationalIndex += 1;
    return nationalIndex >= 1 && nationalIndex <= 7 ? '•' : digit;
  });
}
